import { useState } from "react";
import { useQuery, useMutation } from "react-query";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";
import ListGroup from "react-bootstrap/ListGroup";
import Spinner from "react-bootstrap/Spinner";
import Badge from "react-bootstrap/Badge";
import { toast } from "react-toastify";
import { fetchActionsUtil } from "../../../utils/helpers";
import { useAuth } from "../../../hooks/useAuth";

function SendDocumentsModal({ show, setShow, partner }) {
  const { backendUrl, token, user: authUser } = useAuth();
  const [selectedDocIds, setSelectedDocIds] = useState([]);

  const handleClose = () => {
    setShow(false);
    setSelectedDocIds([]);
  };

  // ── Fetch available partner documents ──────────────────────────────────────
  const {
    data: docsData,
    isLoading: isLoadingDocs,
    isError: isDocsError,
  } = useQuery(
    ["FETCH_PARTNER_DOCS"],
    () => fetchActionsUtil(`${backendUrl}/api/document/partner-doc/get`, "GET"),
    {
      enabled: show, // only fetch when modal is open
      staleTime: 5 * 60 * 1000,
    }
  );

  const documents = docsData?.data ?? [];

  // ── Share selected documents mutation ─────────────────────────────────────
  const shareDocMutation = useMutation(
    payload =>
      fetchActionsUtil(
        `${backendUrl}/api/document/partner-doc/share`,
        "POST",
        token,
        payload
      ),
    {
      onSuccess: () => {
        toast.success(`Sent`);
        handleClose();
      },
      onError: () => {
        toast.error("Unable to send documents. Please try again.");
      },
    }
  );

  const handleToggleDoc = docId => {
    setSelectedDocIds(prev =>
      prev.includes(docId) ? prev.filter(id => id !== docId) : [...prev, docId]
    );
  };

  const handleSelectAll = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map(doc => doc.id));
    }
  };

  const handleSend = () => {
    if (!selectedDocIds.length) {
      toast.warning("Please select at least one document to send.");
      return;
    }

    shareDocMutation.mutate(
      selectedDocIds.map(docId => ({
        docId,
        partnerId: partner?.id,
        docName:
          documents.find(doc => doc.id === docId)?.title ??
          documents.find(doc => doc.id === docId)?.name ??
          "",
      }))
    );
  };

  const allSelected =
    documents.length > 0 && selectedDocIds.length === documents.length;

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="fw-semibold d-flex align-items-center gap-2">
          <span>Send Documents</span>
          {partner?.company_name && (
            <span className="text-muted fs-6 fw-normal">
              {partner.company_name}
            </span>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoadingDocs ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" />
            <span className="ms-3 text-muted">Loading documents…</span>
          </div>
        ) : isDocsError ? (
          <div className="text-center text-danger py-4">
            Failed to load documents. Please close and try again.
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center text-muted py-4">
            No documents available.
          </div>
        ) : (
          <>
            {/* Select-all toggle */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Form.Check
                type="checkbox"
                id="select-all"
                label={
                  <span className="fw-medium">
                    {allSelected ? "Deselect all" : "Select all"}
                  </span>
                }
                checked={allSelected}
                onChange={handleSelectAll}
              />
              {selectedDocIds.length > 0 && (
                <Badge bg="primary" pill>
                  {selectedDocIds.length} selected
                </Badge>
              )}
            </div>

            {/* Document list */}
            <ListGroup variant="flush" className="border rounded">
              {documents.map(doc => {
                const isChecked = selectedDocIds.includes(doc.id);
                return (
                  <ListGroup.Item
                    key={doc.id}
                    action
                    className={`d-flex align-items-center gap-3 py-3 ${
                      isChecked ? "bg-primary bg-opacity-10" : ""
                    }`}
                    style={{ cursor: "pointer" }}
                  >
                    <Form.Check
                      type="checkbox"
                      id={`doc-${doc.id}`}
                      checked={isChecked}
                      onChange={() => handleToggleDoc(doc.id)}
                      onClick={e => e.stopPropagation()}
                      className="mt-1 flex-shrink-0 col-1"
                    />

                    <div
                      onClick={() => handleToggleDoc(doc.id)}
                      className="col-9"
                    >
                      <div className="fw-semibold">{doc.title ?? doc.name}</div>
                      {doc.description && (
                        <div className="text-muted small mt-1">
                          {doc.description}
                        </div>
                      )}
                    </div>

                    <div className="col">
                      <Button
                        size="sm"
                        as="a"
                        target="_blank"
                        href={`${backendUrl}/api/document/partner-doc/view/${
                          doc.id
                        }/${partner.id}`}
                        variant="outline-primary"
                      >
                        Preview
                      </Button>
                    </div>
                  </ListGroup.Item>
                );
              })}
            </ListGroup>
          </>
        )}
      </Modal.Body>

      <Modal.Footer className="border-top">
        <Button variant="outline-secondary" onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSend}
          disabled={
            shareDocMutation.isLoading ||
            selectedDocIds.length === 0 ||
            isLoadingDocs
          }
        >
          {shareDocMutation.isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                className="me-2"
              />
              Sending…
            </>
          ) : (
            `Send${
              selectedDocIds.length > 0 ? ` (${selectedDocIds.length})` : ""
            }`
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SendDocumentsModal;
